import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      const yardageSelect = form.querySelectorAll('select[class*="appearance-none"]')[1] as HTMLSelectElement;
      const addressInput = form.querySelector('input[placeholder="123 Main St, Tacoma, WA"]') as HTMLInputElement;
      await sendFormEmail({
        subject: '253Gravel — New Contact / Quote Request',
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        Email: formData.email,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageSelect?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const inputClass = 'w-full bg-[#3a3a3a] border border-[#555555] rounded-none px-4 py-3 text-sm text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#373737] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-1095">
      <div className="max-w-6xl w-full mx-auto" data-id="element-1096">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-1097">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight" data-id="element-1098">
            Contact & <span className="text-brand-orange" data-id="element-1099">Quotes</span>
          </h1>
          <p className="text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-1100">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1101">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-[#474747] rounded-2xl p-6 md:p-8 border border-[#555555] shadow-lg" data-id="element-1102">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-1103">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-1104">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1105" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2" data-id="element-1106">
                  Request Received
                </h3>
                <p className="text-stone-300 mb-6 max-w-sm text-sm" data-id="element-1107">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-1108">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1109">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1110">
                  <div data-id="element-1111">
                    <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1112">
                      First Name *
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-id="element-1113" />
                  </div>
                  <div data-id="element-1114">
                    <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1115">
                      Last Name *
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-id="element-1116" />
                  </div>
                </div>

                <div data-id="element-1117">
                  <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1118">
                    Email Address *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-1119" />
                </div>

                <div data-id="element-1120">
                  <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1121">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-1122" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1123">
                  <div data-id="element-1124">
                    <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1125">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-1126">
                      <option data-id="element-1127">Crushed Rock</option>
                      <option data-id="element-1128">Pea Gravel</option>
                      <option data-id="element-1129">Drain Rock</option>
                      <option data-id="element-1130">Road Base</option>
                      <option data-id="element-1131">Sand/Topsoil</option>
                      <option data-id="element-1132">Bark & Mulch</option>
                      <option data-id="element-1133">Sand</option>
                      <option data-id="element-1134">River Rock</option>
                      <option data-id="element-1135">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-1136">
                    <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1137">
                      Estimated Yardage
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-1138">
                      <option data-id="element-1139">5 Yards (Minimum)</option>
                      <option data-id="element-1140">10 Yards</option>
                      <option data-id="element-1141">15 Yards</option>
                      <option data-id="element-1142">20+ Yards</option>
                      <option data-id="element-1143">Need help calculating</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-1144">
                  <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1145">
                    Delivery Address *
                  </label>
                  <input type="text" required className={inputClass} placeholder="123 Main St, Tacoma, WA" data-id="element-1146" />
                </div>

                <div data-id="element-1147">
                  <label className="block text-sm font-bold text-stone-200 mb-2" data-id="element-1148">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-1149"></textarea>
                </div>

                <div className="pt-2" data-id="element-1150">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-1151">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1152" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1153" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-1154">
            {/* Contact Info Card */}
            <div className="bg-[#474747] text-white rounded-2xl p-6 border border-[#555555] shadow-lg" data-id="element-1155">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-1156">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-1157">
                <a href="tel:206-619-3742" className="flex items-center group" data-id="element-1158">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-1159">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-1160" />
                  </div>
                  <div data-id="element-1161">
                    <p className="text-xs text-stone-400 mb-0.5" data-id="element-1162">Call Us</p>
                    <p className="font-medium text-sm text-white" data-id="element-1163">
                      (206) 619-3742
                    </p>
                  </div>
                </a>
                <a href="mailto:info@253gravel.com" className="flex items-center group" data-id="element-1164">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-1165">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-1166" />
                  </div>
                  <div data-id="element-1167">
                    <p className="text-xs text-stone-400 mb-0.5" data-id="element-1168">Email Us</p>
                    <p className="font-medium text-sm text-white" data-id="element-1169">
                      info@253gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-1170">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-1171">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-1172" />
                  </div>
                  <div data-id="element-1173">
                    <p className="text-xs text-stone-400 mb-0.5" data-id="element-1174">Location</p>
                    <p className="font-medium text-sm text-white" data-id="element-1175">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#474747] rounded-2xl p-6 border border-[#555555] shadow-lg" data-id="element-1176">
              <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center" data-id="element-1177">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-1178" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-1179">
                <div className="flex justify-between items-center pb-2 border-b border-[#555555]" data-id="element-1180">
                  <span className="text-stone-300" data-id="element-1181">Mon - Fri</span>
                  <span className="font-medium text-white" data-id="element-1182">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-[#555555]" data-id="element-1183">
                  <span className="text-stone-300" data-id="element-1184">Saturday</span>
                  <span className="font-medium text-white" data-id="element-1185">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1186">
                  <span className="text-stone-300" data-id="element-1187">Sunday</span>
                  <span className="font-medium text-brand-orange" data-id="element-1188">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-1189">
              <h4 className="font-heading text-sm font-bold text-white mb-2" data-id="element-1190">
                Service Area
              </h4>
              <p className="text-xs text-stone-300 leading-relaxed" data-id="element-1191">
                Proudly serving Tacoma, Puyallup, Bonney Lake, Federal Way,
                Auburn, Kent, Lakewood, and surrounding South King County
                communities with reliable, same-day and next-day delivery. Not
                sure if you're in our delivery zone? Give us a call — we're
                happy to help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}